import React, { useState, useEffect } from "react";
import Footer from "./Footer";
import { CircleX, X } from "lucide-react";
import SafeHTML from "../../../components/SafeHTML";
import * as LucideIcons from "lucide-react";
import useStickyButton from "../hooks/useStickyButton";

const MultipleReject = ({ progressStates, onRetry, uiConfig }) => {
  const [isMyKadFrontFailed, setIsMyKadFrontFailed] = useState();
  const [isMyKadBackFailed, setIsMyKadBackFailed] = useState();
  const [isSelfieFailed, setIsSelfieFailed] = useState();
  const [isHologramFailed, setIsHologramFailed] = useState();
  const { buttonContainerRef, sentinelRef, isSticky, buttonHeight } = useStickyButton();

  // Safe access
  const global = uiConfig?.Global || {};
  const multipleReject = uiConfig?.MultipleReject || {};
  const cardBgColor = global?.card?.backgroundColor || "#f3f4f6"; // gray-50
  const textColor = global?.text?.fontColor || "#6b7280"; // default gray-500
  const cardOpacity = global?.card?.opacity || "1"; // default opacity
  const buttonBgColor = global?.button?.backgroundColor || "#2563eb"; // blue-600
  const buttonTextColor = global?.button?.fontColor || "#ffffff"; // white
  const pageBgColor = global?.pageContainer?.backgroundColor || "#ffffff"; // white
  const showFooter = global?.Footer?.Show !== undefined ? global.Footer.Show : true;

  const getTitleHTML = () => {
    const titleConfig = multipleReject?.Title?.Text;
    if (titleConfig) {
      return titleConfig;
    }
    return `<p style="font-size: 1.5rem; font-weight: 500; line-height: 2rem; color: ${textColor};">
      Oops! We couldn't verify your identity
    </p>`;
  };

  const getDescriptionHTML = () => {
    const descriptionConfig = multipleReject?.Description?.Text;
    if (descriptionConfig) {
      return descriptionConfig;
    }
    return `<p style="font-size: 1rem; margin-top: 1rem; color: ${textColor};">
      There are <strong>multiple issues </strong>with your MyKad. Please verify and try again.
    </p>`;
  };

  const getButtonConfig = () => {
    const globalButton = global?.button || {};
    const buttonConfig = multipleReject?.TryAgainButton || {};
    
    // Build base style from global button config
    const globalStyle = globalButton?.Style || `width: 66.666667%; padding: 0.75rem 1rem; border-radius: 9999px; display: flex; align-items: center; justify-content: center; gap: 0.5rem; margin: 0 auto; background: ${buttonBgColor}; color: ${buttonTextColor}; transition: transform 0.15s ease-in-out; border: none; cursor: pointer;`;
    
    // Parse global style
    const parsedGlobalStyle = globalStyle.split(';').reduce((acc, style) => {
      const [key, value] = style.split(':').map(s => s.trim());
      if (key && value) {
        acc[key] = value;
      }
      return acc;
    }, {});
    
    // Parse and merge specific button style if it exists
    let finalStyle = { ...parsedGlobalStyle };
    if (buttonConfig?.Style) {
      const parsedButtonStyle = buttonConfig.Style.split(';').reduce((acc, style) => {
        const [key, value] = style.split(':').map(s => s.trim());
        if (key && value) {
          acc[key] = value;
        }
        return acc;
      }, {});
      finalStyle = { ...finalStyle, ...parsedButtonStyle };
    }
    
    // Convert back to CSS string
    const mergedStyleString = Object.entries(finalStyle)
      .map(([key, value]) => `${key}: ${value}`)
      .join('; ') + ';';
    
    return {
      text: buttonConfig?.Text || globalButton?.Text || "Try Again",
      style: mergedStyleString,
      icon: {
        show: buttonConfig?.Icon?.Show !== undefined ? buttonConfig.Icon.Show : (globalButton?.Icon?.Show !== undefined ? globalButton.Icon.Show : false),
        position: buttonConfig?.Icon?.Position || globalButton?.Icon?.Position || "end",
        name: buttonConfig?.Icon?.Name || globalButton?.Icon?.Name || "RotateCcw",
        className: buttonConfig?.Icon?.ClassName || globalButton?.Icon?.ClassName || "ml-2"
      }
    };
  };

  useEffect(() => {
    if (progressStates.myKadFront === "Fail") {
      setIsMyKadFrontFailed(true);
    }
    if (progressStates.myKadBack === "Fail") {
      setIsMyKadBackFailed(true);
    }
    if (progressStates.selfieWithMyKad === "Fail") {
      setIsSelfieFailed(true);
    }
    if (progressStates.hologramDetection === "Fail") {
      setIsHologramFailed(true);
    }
  }, [progressStates]);

  return (
    <div
      style={{
        background: pageBgColor,
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-start",
        padding: "1rem",
        height: "100%",
      }}
    >
      <div
        style={{
          background: cardBgColor,
          opacity: cardOpacity,
          borderRadius: "0.75rem",
          padding: "1rem",
          marginBottom: "1.5rem",
        }}
      >
        <div className="flex flex-col items-center mb-8">
          <CircleX height="72" width="72" color="red" />
        </div>
        <div className="flex flex-col">
          <SafeHTML html={getTitleHTML()} />
          <SafeHTML html={getDescriptionHTML()} />

          <ul className="space-y-2 my-8">
            {isMyKadFrontFailed && (
              <li className="flex items-start space-x-2">
                <X className="w-6 h-6 text-red-600" />
                <span>MyKad front</span>
              </li>
            )}
            {isMyKadBackFailed && (
              <li className="flex items-start space-x-2">
                <X className="w-6 h-6 text-red-600" />
                <span>MyKad back</span>
              </li>
            )}
            {isSelfieFailed && (
              <li className="flex items-start space-x-2">
                <X className="w-6 h-6 text-red-600" />
                <span>Selfie with MyKad</span>
              </li>
            )}
            {isHologramFailed && (
              <li className="flex items-start space-x-2">
                <X className="w-6 h-6 text-red-600" />
                <span>Hologram detection</span>
              </li>
            )}
          </ul>
          {<ul className="text-gray-600 text-md mt-4"></ul>}
        </div>
      </div>

      <div className="flex flex-1" />

      {/* Sentinel element for Intersection Observer */}
      <div ref={sentinelRef} style={{ height: "1px", visibility: "hidden" }} />

      {/* Spacer to maintain space when button is sticky */}
      {isSticky && buttonHeight > 0 && (
        <div style={{ height: `${buttonHeight}px`, width: "100%" }} />
      )}

      <div 
        ref={buttonContainerRef}
        style={{
          position: "sticky",
          bottom: "0",
          left: "0",
          right: "0",
          width: "100%",
          padding: "1rem",
          background: "transparent",
          zIndex: "20",
          transition: "all 0.3s ease-in-out",
          marginTop: "auto",
        }}
      >
        {(() => {
          const btnConfig = getButtonConfig();
          const IconComponent = btnConfig.icon.show && LucideIcons[btnConfig.icon.name] ? LucideIcons[btnConfig.icon.name] : null;
          
          const buttonStyle = btnConfig.style.split(';').reduce((acc, style) => {
            const [key, value] = style.split(':').map(s => s.trim());
            if (key && value) {
              const camelKey = key.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
              acc[camelKey] = value;
            }
            return acc;
          }, {});

          return (
            <button
              style={{ ...buttonStyle, pointerEvents: 'auto' }}
              onClick={onRetry}
              onMouseDown={(e) => e.currentTarget.style.transform = 'scale(0.95)'}
              onMouseUp={(e) => e.currentTarget.style.transform = 'scale(1)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
              onTouchStart={(e) => e.currentTarget.style.transform = 'scale(0.95)'}
              onTouchEnd={(e) => e.currentTarget.style.transform = 'scale(1)'}
            >
              {btnConfig.icon.position === "start" && IconComponent && (
                <IconComponent className={btnConfig.icon.className} />
              )}
              <span>{btnConfig.text}</span>
              {btnConfig.icon.position === "end" && IconComponent && (
                <IconComponent className={btnConfig.icon.className} />
              )}
            </button>
          );
        })()}
      </div>

      {showFooter ? (
        <Footer
          style={{
            fontSize: "0.75rem",
            color: textColor,
            marginTop: "1rem",
          }}
        />
      ) : (
        /* Minimal spacer to prevent sticky flickering when footer is hidden */
        <div style={{ height: "5px", flexShrink: 0 }} />
      )}
    </div>
  );
};

export default MultipleReject;
